/**
 * @reflecto/client-core — conversations/index.ts
 *
 * Conversation thread storage logic — shared between the extension
 * (`browser.storage.local`) and the web app (`idb-keyval`/IndexedDB).
 * Pure business logic over a `StorageAdapter`; no platform APIs.
 *
 * Exposed via `createConversations(storage)`, which closes over the
 * adapter and returns the same set of operations the original
 * extension implementation provided. The factory pattern lets each
 * host instantiate a separate module instance with its own adapter
 * and module-level reverse index, without sharing state across
 * processes (the extension's service worker and the web app are
 * different runtime contexts).
 */

import type { StorageAdapter } from "../storage/adapter";
import type {
  ConversationMessage,
  ConversationThread,
  NotificationAction,
} from "../storage-schema";

const MAX_HASHES_PER_CONVERSATION = 100;
const MAX_MESSAGES_PER_CONVERSATION = 100;
const MAX_CONVERSATIONS = 200;
const MESSAGE_TTL_MS = 72 * 60 * 60 * 1000; // 72 hours

export interface ConversationMeta {
  title: string;
  app: string;
  packageName: string;
  isGroup: boolean;
  groupName: string | null;
  actions?: NotificationAction[];
}

export interface ConversationsApi {
  /** Ensure the reverse index is populated. Call before lookupConvKeyByMsgId. */
  ensureIndexLoaded(): Promise<void>;
  /** O(1) lookup of which conversation a msgId belongs to. Returns null if not found. */
  lookupConvKeyByMsgId(msgId: string): string | null;
  /**
   * Atomic check-and-add: dedup by `message.id` AND append to conversation
   * thread in a single get/set cycle. Prevents race conditions when two
   * messages arrive back-to-back for the same conversation.
   *
   * `message.id` is the dedup hash — it's also what `pruneExpired` filters
   * `seenConversationHashes` against. The previous API accepted a separate
   * `messageHash` parameter; both production callers wrote it from the same
   * source as `message.id`, so the parameter was eliminated to remove the
   * class of invariant violations where the two could diverge.
   */
  addConversationMessageIfNew(
    conversationKey: string,
    message: ConversationMessage,
    meta: ConversationMeta
  ): Promise<{ isNew: boolean; thread?: ConversationThread }>;
  /** Return all conversation threads sorted by lastMessageTimestamp descending. */
  getConversations(): Promise<ConversationThread[]>;
  /** Return a single conversation thread by key, or null if not found. */
  getConversation(key: string): Promise<ConversationThread | null>;
  /**
   * Mark all messages in a conversation as read: reset the thread's
   * unreadCount to 0 and decrement the global `unreadCount` storage
   * counter accordingly. Note this only updates storage — actual
   * badge rendering (toolbar badge in the extension,
   * `navigator.setAppBadge()` in the web app) is the host's
   * responsibility, driven off the storage value.
   */
  markConversationRead(key: string): Promise<void>;
  /** Remove a conversation thread by key and clean up its hash entries. */
  removeConversation(key: string): Promise<void>;
  /** Remove multiple conversation threads in a single storage round-trip. */
  removeConversations(keys: string[]): Promise<void>;
  /**
   * Remove a single message (by `msgId`) from whichever conversation
   * thread contains it. If the thread becomes empty afterwards the
   * thread itself is removed (delegating to `removeConversation` so
   * `seenConversationHashes`, `unreadCount`, and the reverse index
   * are all kept in sync). If the thread keeps other messages, only
   * the matching message is dropped, the thread's `unreadCount` is
   * clamped to the new message count (without ever going negative)
   * and the global `unreadCount` is decremented if the removed
   * message was unread, the per-thread `seenConversationHashes`
   * entry is filtered to drop the orphan hash, and the in-memory
   * `msgIdIndex` is updated.
   *
   * Returns `true` if the msgId was found and removed, `false` if
   * no thread contained it (i.e. the call was a no-op).
   *
   * Hosts use this when the SERVER pushes a `dismiss` event — the
   * paired Android dismissed the underlying notification and the
   * client should mirror the removal locally without going through
   * the server-roundtrip dismiss endpoint.
   */
  removeMessageByMsgId(msgId: string): Promise<boolean>;
  /**
   * Remove messages older than MESSAGE_TTL_MS and delete empty conversation threads.
   * Also removes stale seenConversationHashes entries for deleted threads and
   * decrements global unreadCount for any removed unread threads.
   */
  pruneExpired(): Promise<void>;
}

/**
 * Create a conversations module bound to a specific StorageAdapter.
 * Each call returns its own private reverse index — host applications
 * typically call this once at startup and re-export the operations.
 */
export function createConversations(storage: StorageAdapter): ConversationsApi {
  // In-memory reverse index: msgId → conversationKey for O(1) dismiss lookup.
  // Rebuilt lazily on host restart via ensureIndexLoaded().
  const msgIdIndex = new Map<string, string>();
  let indexLoaded = false;
  // De-dupe concurrent ensureIndexLoaded() callers: two callers
  // arriving before the first storage round-trip resolves would
  // otherwise both run rebuildIndex(). Benign in the original (the
  // second call ends in the correct state) but wasteful — share
  // the in-flight promise so only one read happens.
  let loadingPromise: Promise<void> | null = null;

  function rebuildIndex(store: Record<string, ConversationThread>): void {
    msgIdIndex.clear();
    for (const [convKey, thread] of Object.entries(store)) {
      for (const m of thread.messages) {
        msgIdIndex.set(m.msgId, convKey);
      }
    }
    indexLoaded = true;
  }

  async function ensureIndexLoaded(): Promise<void> {
    if (indexLoaded) return;
    if (loadingPromise) return loadingPromise;
    loadingPromise = (async () => {
      try {
        const { conversations } = await storage.get("conversations");
        rebuildIndex(conversations ?? {});
      } finally {
        loadingPromise = null;
      }
    })();
    return loadingPromise;
  }

  function lookupConvKeyByMsgId(msgId: string): string | null {
    return msgIdIndex.get(msgId) ?? null;
  }

  async function addConversationMessageIfNew(
    conversationKey: string,
    message: ConversationMessage,
    meta: ConversationMeta
  ): Promise<{ isNew: boolean; thread?: ConversationThread }> {
    // Block on ensureIndexLoaded() before reading storage. Without
    // this barrier, an in-flight ensureIndexLoaded() could read a
    // pre-write snapshot and rebuild the reverse index AFTER our
    // write+rebuild lands — clobbering this message's msgId out of
    // the index until the next rebuild. Awaiting here serialises
    // the index load against any subsequent mutation.
    await ensureIndexLoaded();

    // message.id is the dedup hash — see ConversationsApi JSDoc.
    const messageHash = message.id;

    const { conversations, seenConversationHashes } = await storage.get(
      "conversations",
      "seenConversationHashes"
    );
    const allHashes = seenConversationHashes ?? {};
    const hashes = allHashes[conversationKey] ?? [];

    // Dedup check
    if (hashes.includes(messageHash)) return { isNew: false };

    // Register hash
    hashes.push(messageHash);
    if (hashes.length > MAX_HASHES_PER_CONVERSATION) {
      hashes.splice(0, hashes.length - MAX_HASHES_PER_CONVERSATION);
    }
    allHashes[conversationKey] = hashes;

    // Append message to conversation
    const store = conversations ?? {};
    const existing = store[conversationKey];

    if (existing) {
      existing.messages.push(message);
      // Cap stored messages — evict oldest when over limit
      if (existing.messages.length > MAX_MESSAGES_PER_CONVERSATION) {
        existing.messages.splice(0, existing.messages.length - MAX_MESSAGES_PER_CONVERSATION);
      }
      existing.messages.sort((a, b) => a.timestamp - b.timestamp);
      existing.lastMessageTimestamp = existing.messages[existing.messages.length - 1].timestamp;
      existing.lastUpdatedAt = Date.now();
      existing.unreadCount += 1;
      existing.title = meta.title;
      existing.actions = meta.actions; // overwrite even if undefined — clears stale actions
    } else {
      store[conversationKey] = {
        key: conversationKey,
        title: meta.title,
        app: meta.app,
        packageName: meta.packageName,
        isGroup: meta.isGroup,
        groupName: meta.groupName,
        messages: [message],
        lastMessageTimestamp: message.timestamp,
        lastUpdatedAt: Date.now(),
        unreadCount: 1,
        actions: meta.actions,
      };
    }

    // Evict oldest conversations if over cap
    const keys = Object.keys(store);
    if (keys.length > MAX_CONVERSATIONS) {
      const sorted = keys
        .map((k) => ({ key: k, lastUpdatedAt: store[k].lastUpdatedAt }))
        .sort((a, b) => a.lastUpdatedAt - b.lastUpdatedAt);
      const toEvict = sorted.slice(0, keys.length - MAX_CONVERSATIONS);
      for (const { key } of toEvict) {
        delete store[key];
        delete allHashes[key];
      }
    }

    // Single atomic write for both hashes and conversations
    await storage.set({ conversations: store, seenConversationHashes: allHashes });

    // Keep reverse index in sync — rebuild covers evictions and message cap trims
    rebuildIndex(store);

    return { isNew: true, thread: store[conversationKey] };
  }

  async function getConversations(): Promise<ConversationThread[]> {
    const { conversations } = await storage.get("conversations");
    const store = conversations ?? {};
    return Object.values(store).sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);
  }

  async function getConversation(key: string): Promise<ConversationThread | null> {
    const { conversations } = await storage.get("conversations");
    return conversations?.[key] ?? null;
  }

  async function markConversationRead(key: string): Promise<void> {
    const { conversations, unreadCount } = await storage.get("conversations", "unreadCount");
    const store = conversations ?? {};
    if (store[key] && store[key].unreadCount > 0) {
      const decrement = store[key].unreadCount;
      store[key].unreadCount = 0;
      await storage.set({
        conversations: store,
        unreadCount: Math.max(0, (unreadCount ?? 0) - decrement),
      });
    }
  }

  async function removeConversation(key: string): Promise<void> {
    await removeConversations([key]);
  }

  async function removeConversations(keys: string[]): Promise<void> {
    if (keys.length === 0) return;
    const { conversations, seenConversationHashes, unreadCount } = await storage.get(
      "conversations",
      "seenConversationHashes",
      "unreadCount"
    );
    const store = conversations ?? {};
    const hashes = seenConversationHashes ?? {};

    let unreadDecrement = 0;
    // Collect msgIds for index cleanup before deleting threads from the store
    const deletedMsgIds: string[] = [];
    let anyDeleted = false;
    for (const key of keys) {
      const thread = store[key];
      if (!thread) continue;
      anyDeleted = true;
      unreadDecrement += thread.unreadCount;
      if (indexLoaded) {
        for (const m of thread.messages) deletedMsgIds.push(m.msgId);
      }
      delete store[key];
      delete hashes[key];
    }

    if (!anyDeleted) return;

    await storage.set({
      conversations: store,
      seenConversationHashes: hashes,
      unreadCount: Math.max(0, (unreadCount ?? 0) - unreadDecrement),
    });

    // Remove deleted conversations' msgIds from the reverse index
    for (const msgId of deletedMsgIds) {
      msgIdIndex.delete(msgId);
    }
  }

  async function removeMessageByMsgId(msgId: string): Promise<boolean> {
    const { conversations, seenConversationHashes, unreadCount } = await storage.get(
      "conversations",
      "seenConversationHashes",
      "unreadCount"
    );
    const store = conversations ?? {};
    const hashes = seenConversationHashes ?? {};

    // Find the owning thread. Fast path: if the in-memory reverse
    // index has been loaded (`ensureIndexLoaded` ran or a prior
    // `addConversationMessageIfNew` rebuilt it), `lookupConvKeyByMsgId`
    // is O(1). Cold path: walk the store. Bounded by MAX_CONVERSATIONS
    // (200) × MAX_MESSAGES_PER_CONVERSATION (100), so the cold scan
    // is cheap — but the warm path is the common case once the page
    // has subscribed to anything.
    //
    // Verify the index hit against the actual store: the reverse
    // index is rebuilt on every mutation but a stale entry could
    // theoretically slip through (e.g. concurrent caller mid-rebuild),
    // and we don't want to fail with a confident "not found" when the
    // message is still there.
    let owningKey: string | null = null;
    const indexHit = lookupConvKeyByMsgId(msgId);
    if (indexHit && store[indexHit]?.messages.some((m) => m.msgId === msgId)) {
      owningKey = indexHit;
    } else {
      for (const [key, thread] of Object.entries(store)) {
        if (thread.messages.some((m) => m.msgId === msgId)) {
          owningKey = key;
          break;
        }
      }
    }
    if (!owningKey) return false;

    const thread = store[owningKey];
    const targetIndex = thread.messages.findIndex((m) => m.msgId === msgId);
    const target = thread.messages[targetIndex];
    const remaining = thread.messages.filter((m) => m.msgId !== msgId);

    if (remaining.length === 0) {
      // Empty thread — delegate to removeConversation, which owns
      // hash-array cleanup, unreadCount decrement, and msgIdIndex
      // sync. Avoids duplicating that logic and keeps the
      // single-message and whole-thread paths consistent.
      await removeConversation(owningKey);
      return true;
    }

    // Determine if the removed message fell inside the per-thread
    // unread window. Messages are stored oldest-first and unread =
    // the last `unreadCount` entries (markConversationRead clears
    // the counter; addConversationMessageIfNew bumps it for each
    // new arrival). So a message at index >= (length - unreadCount)
    // was still unread when it was dropped — both the per-thread
    // and the global counter need to decrement by one.
    //
    // Bug history: previously this was `thread.unreadCount > remaining.length`,
    // which was true only when EVERY message was unread. Mixed
    // threads (e.g. 5 messages, 3 unread) wrongly stayed at the
    // pre-removal count, silently drifting the global badge.
    //
    // Math.max(0, ...) defends against a corrupted invariant
    // (`unreadCount > messages.length`). Without the clamp, the
    // window-start would go negative, every index would satisfy
    // `>= unreadWindowStart`, and removing any message — even a
    // read one — would falsely decrement the global counter.
    const unreadWindowStart = Math.max(0, thread.messages.length - thread.unreadCount);
    const wasUnread = target !== undefined && targetIndex >= unreadWindowStart;

    // Thread survives — drop just this message + its hash entry,
    // adjust unreadCount + global, and update the reverse index.
    const nextUnread = Math.min(
      remaining.length,
      Math.max(0, thread.unreadCount - (wasUnread ? 1 : 0))
    );
    const updatedThread: ConversationThread = {
      ...thread,
      messages: remaining,
      unreadCount: nextUnread,
      lastMessageTimestamp: remaining[remaining.length - 1].timestamp,
      // lastUpdatedAt is the eviction-sort anchor — `addConversationMessageIfNew`
      // sorts by it when MAX_CONVERSATIONS is exceeded. A removal is a
      // mutation; not bumping it would leave a recently-modified thread
      // looking stale to the LRU eviction pass.
      lastUpdatedAt: Date.now(),
    };
    store[owningKey] = updatedThread;

    // seenConversationHashes entries equal `ConversationMessage.id` by
    // construction (addConversationMessageIfNew writes both from
    // message.id), so the orphan filter is keyed on that.
    const targetHashId = target?.id;
    const existingHashes = hashes[owningKey] ?? [];
    if (targetHashId && existingHashes.length > 0) {
      const filtered = existingHashes.filter((h) => h !== targetHashId);
      if (filtered.length !== existingHashes.length) {
        hashes[owningKey] = filtered;
      }
    }

    await storage.set({
      conversations: store,
      seenConversationHashes: hashes,
      unreadCount: wasUnread ? Math.max(0, (unreadCount ?? 0) - 1) : (unreadCount ?? 0),
    });

    msgIdIndex.delete(msgId);
    return true;
  }

  async function pruneExpired(): Promise<void> {
    const { conversations, seenConversationHashes, unreadCount } = await storage.get(
      "conversations",
      "seenConversationHashes",
      "unreadCount"
    );
    const store = conversations ?? {};
    const hashes = seenConversationHashes ?? {};

    const cutoff = Date.now() - MESSAGE_TTL_MS;
    const keysToRemove: string[] = [];
    let unreadDecrement = 0;
    let dirty = false;

    for (const [key, conv] of Object.entries(store)) {
      const before = conv.messages.length;
      conv.messages = conv.messages.filter((m) => m.receivedAt > cutoff);
      if (conv.messages.length !== before) dirty = true;
      if (conv.messages.length === 0) {
        keysToRemove.push(key);
        unreadDecrement += conv.unreadCount;
      } else {
        conv.lastMessageTimestamp = conv.messages[conv.messages.length - 1].timestamp;
        // unreadCount can drift above messages.length:
        // addConversationMessageIfNew unconditionally increments
        // unreadCount but the MAX_MESSAGES_PER_CONVERSATION splice
        // does not, so a conversation with sustained traffic over
        // the cap accumulates stale unread credit. Clamp here, and
        // mark the store dirty even when no messages expired this
        // tick — otherwise the no-op-write optimisation below would
        // strand the corrected value in memory and let the drift
        // re-emerge on the next addConversationMessageIfNew round-trip.
        const prevUnread = conv.unreadCount;
        conv.unreadCount = Math.min(conv.unreadCount, conv.messages.length);
        if (prevUnread !== conv.unreadCount) dirty = true;
        unreadDecrement += prevUnread - conv.unreadCount;
        // Drop hash entries for messages that were just pruned away.
        // Without this, seenConversationHashes accumulates stale hashes
        // for messages no longer in storage and the per-conversation
        // arrays drift toward MAX_HASHES_PER_CONVERSATION (100) without
        // any live messages needing the dedup protection. seenConversationHashes
        // entries equal `ConversationMessage.id` by construction —
        // addConversationMessageIfNew writes both from `message.id`.
        const survivingIds = new Set(conv.messages.map((m) => m.id));
        const existingHashes = hashes[key] ?? [];
        if (existingHashes.length > 0) {
          const filtered = existingHashes.filter((h) => survivingIds.has(h));
          if (filtered.length !== existingHashes.length) {
            hashes[key] = filtered;
            dirty = true;
          }
        }
      }
    }

    for (const key of keysToRemove) {
      delete store[key];
      delete hashes[key];
      dirty = true;
    }

    // Skip the round-trip if nothing changed. pruneExpired runs on a
    // schedule and is a no-op most invocations once a conversation
    // store has stabilised — writing every time would make the alarm
    // tick wake the storage layer for nothing.
    if (!dirty) return;

    await storage.set({
      conversations: store,
      seenConversationHashes: hashes,
      unreadCount: Math.max(0, (unreadCount ?? 0) - unreadDecrement),
    });

    // Pruning can remove messages from the middle of threads — full rebuild is simplest
    rebuildIndex(store);
  }

  return {
    ensureIndexLoaded,
    lookupConvKeyByMsgId,
    addConversationMessageIfNew,
    getConversations,
    getConversation,
    markConversationRead,
    removeConversation,
    removeConversations,
    removeMessageByMsgId,
    pruneExpired,
  };
}
